import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarCheck, X } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { PageHero } from "./About";
import { ScrollReveal } from "../components/ScrollReveal";
import { ServiceIcon } from "../components/ServiceIcon";

export default function Services() {
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const [active, setActive] = useState(null);
  const medical = services.filter((s) => s.category !== "diagnostic").sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div>
      <PageHero eyebrow="Medical Services" title="Our Medical Services" subtitle="Comprehensive, compassionate healthcare for individuals and families of every age." />
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {medical.map((s, i) => (
              <ScrollReveal key={s.id} delay={i * 0.05}>
                <button onClick={() => setActive(s)} className="card-lift text-left w-full h-full bg-white rounded-2xl border border-slate-100 p-7 group" data-testid={`service-card-${i}`}>
                  <div className="w-14 h-14 rounded-2xl gradient-bg-soft flex items-center justify-center text-[#1A75BC] mb-5 group-hover:scale-110 transition-transform">
                    <ServiceIcon name={s.icon} size={26} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F1E36] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#627D98] leading-relaxed">{s.short_description}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-[#1A75BC]">Learn more <ArrowRight size={15} /></span>
                </button>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/diagnostics" className="inline-flex items-center gap-2 text-[#27AE60] font-bold">Looking for X-Ray & Diagnostics? <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ScrollReveal className="bg-[#F0F7FB] rounded-3xl p-7 sm:p-10 border border-sky-100">
            <p className="eyebrow mb-3">General Consultation</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F1E36]">Health concerns you can discuss with the doctor</h2>
            <p className="mt-4 text-[#627D98] leading-relaxed">Consultation enquiries may include the following concerns. The doctor will assess each patient and explain the appropriate next step.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Diabetes", "High or low blood sugar", "High or low blood pressure", "Hyperthyroidism or hypothyroidism", "High cholesterol", "Weight gain", "Fever", "Infections", "Dengue", "Anaemia", "Headache", "Giddiness", "Tiredness", "Abdominal pain", "Loose motions", "Constipation", "Joint pain"].map((topic) => (
                <span key={topic} className="bg-white border border-sky-100 rounded-full px-4 py-2 text-sm font-semibold text-[#334E68]">{topic}</span>
              ))}
            </div>
            <p className="mt-6 text-sm text-[#627D98]">Doctor: General Physician and Family Doctor — MBBS, MD, Diploma in Diabetology. Clinic hours: Monday–Saturday, 6:00 PM–9:00 PM.</p>
          </ScrollReveal>
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActive(null)} data-testid="service-modal">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} className="absolute top-5 right-5 text-slate-400 hover:text-slate-700" data-testid="service-modal-close"><X size={22} /></button>
            <div className="w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center text-white mb-5"><ServiceIcon name={active.icon} size={30} /></div>
            <h3 className="text-2xl font-extrabold text-[#0F1E36] mb-3">{active.title}</h3>
            <p className="text-[#627D98] leading-relaxed">{active.description}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-6 gradient-bg btn-glow text-white font-bold px-6 py-3 rounded-full"><CalendarCheck size={18} /> Book Appointment</Link>
          </div>
        </div>
      )}
    </div>
  );
}
